/*
 * QQQ - Low-code Application Framework for Engineers.
 * Copyright (C) 2021-2026.  Kingsrook, LLC
 * 651 N Broad St Ste 205 # 6917 | Middletown DE 19709 | United States
 * contact@kingsrook.com
 * https://github.com/Kingsrook/
 *
 * This program is free software: you can redistribute it and/or modify
 * it under the terms of the GNU Affero General Public License as
 * published by the Free Software Foundation, either version 3 of the
 * License, or (at your option) any later version.
 *
 * This program is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
 * GNU Affero General Public License for more details.
 *
 * You should have received a copy of the GNU Affero General Public License
 * along with this program.  If not, see <https://www.gnu.org/licenses/>.
 */

const DATABASE_NAME = "qqqProcessFilters";
const DATABASE_VERSION = 1;
const FILTER_JSON_STORE = "filterJSON";
const FILTER_INFO_STORE = "filterInfo";

export const PROCESS_FILTER_STORAGE_TIMEOUT_MS = 5000;
/*** One day, and every filter is deleted on logout:  filter criteria can hold
 ** names, emails, or order numbers, which the next person to use this browser
 ** shouldn't be able to read. ***/
export const MAX_STORED_FILTER_AGE_MS = 24 * 60 * 60 * 1000;
export const MAX_STORED_FILTER_TOTAL_LENGTH = 20_000_000;

/*** How many of this page's most recent filters to also keep in memory - which
 ** is how a process launched in-app reads its filter without waiting for the
 ** IndexedDB write.  Memory is cleared by a reload; IndexedDB covers that. ***/
export const MAX_IN_MEMORY_FILTERS = 5;

/*** What the info store keeps per filter - read on every write to decide what
 ** to evict, so that eviction never has to load the filters themselves. ***/
interface StoredFilterInfo
{
   key: string;
   storedAt: number;
   length: number;
}


/*******************************************************************************
 ** Filters passed to processes, too large to put in a URL, stored in the
 ** browser's IndexedDB under a key that the URL carries instead.  IndexedDB is
 ** shared by every tab of the origin and survives a restart, so the URL reloads
 ** and opens in any tab of the same browser (but not someone else's).
 **
 ** Entries are evicted, on each write, once older than MAX_STORED_FILTER_AGE_MS,
 ** and oldest-first once the total length would pass
 ** MAX_STORED_FILTER_TOTAL_LENGTH.  A read also refuses an entry past the max
 ** age, since a write may not have happened since.  Logging out deletes them
 ** all.
 **
 ** storeFilterJSONUnderKey also keeps the page's most recent filters in memory,
 ** so a process launched in-app reads its filter at once, without waiting on
 ** (or depending on) the IndexedDB write.
 *******************************************************************************/
export default class ProcessFilterStorage
{
   private static inMemoryFilters: Map<string, {filterJSON: string, storedAt: number}> = new Map();


   /*******************************************************************************
    ** Store a filter's JSON, and return the key to read it back by.  Rejects if
    ** IndexedDB is unavailable or the write fails.
    *******************************************************************************/
   public static async storeFilterJSON(filterJSON: string, maxTotalLength: number = MAX_STORED_FILTER_TOTAL_LENGTH, maxAgeMs: number = MAX_STORED_FILTER_AGE_MS): Promise<string>
   {
      const key = ProcessFilterStorage.makeKey();
      await ProcessFilterStorage.withTimeout(ProcessFilterStorage.storeFilterJSONWithoutTimeout(key, filterJSON, maxTotalLength, maxAgeMs));
      return (key);
   }


   /*******************************************************************************
    ** Store a filter's JSON under a key the caller already made (with makeKey) -
    ** e.g., one already put in a URL.  It is readable from memory at once (for
    ** the rest of this page's life), and from IndexedDB once the returned promise
    ** resolves.  Rejects if IndexedDB is unavailable or the write fails.
    *******************************************************************************/
   public static storeFilterJSONUnderKey(key: string, filterJSON: string): Promise<void>
   {
      ProcessFilterStorage.inMemoryFilters.set(key, {filterJSON, storedAt: Date.now()});
      while (ProcessFilterStorage.inMemoryFilters.size > MAX_IN_MEMORY_FILTERS)
      {
         ProcessFilterStorage.inMemoryFilters.delete(ProcessFilterStorage.inMemoryFilters.keys().next().value);
      }

      return ProcessFilterStorage.withTimeout(ProcessFilterStorage.storeFilterJSONWithoutTimeout(key, filterJSON, MAX_STORED_FILTER_TOTAL_LENGTH, MAX_STORED_FILTER_AGE_MS));
   }


   /*******************************************************************************
    ** Do the IndexedDB work of storeFilterJSON and storeFilterJSONUnderKey.  This
    ** always closes the database once it opens - even if that is after the
    ** caller's timeout has fired.
    *******************************************************************************/
   private static async storeFilterJSONWithoutTimeout(key: string, filterJSON: string, maxTotalLength: number, maxAgeMs: number): Promise<void>
   {
      const database = await ProcessFilterStorage.openDatabase();
      try
      {
         await new Promise<void>((resolve, reject) =>
         {
            const transaction = database.transaction([FILTER_JSON_STORE, FILTER_INFO_STORE], "readwrite");
            transaction.oncomplete = () => resolve();
            transaction.onerror = (event) => reject(ProcessFilterStorage.getTransactionError(transaction, event));
            transaction.onabort = (event) => reject(ProcessFilterStorage.getTransactionError(transaction, event));

            const filterJSONStore = transaction.objectStore(FILTER_JSON_STORE);
            const filterInfoStore = transaction.objectStore(FILTER_INFO_STORE);

            const getAllRequest = filterInfoStore.getAll();
            getAllRequest.onsuccess = () =>
            {
               try
               {
                  const now = Date.now();
                  const keysToDelete = ProcessFilterStorage.chooseKeysToEvict(getAllRequest.result as StoredFilterInfo[], filterJSON.length, now, maxTotalLength, maxAgeMs);
                  for (const keyToDelete of keysToDelete)
                  {
                     filterJSONStore.delete(keyToDelete);
                     filterInfoStore.delete(keyToDelete);
                  }

                  filterJSONStore.put(filterJSON, key);
                  filterInfoStore.put({key, storedAt: now, length: filterJSON.length} as StoredFilterInfo, key);
               }
               catch (e)
               {
                  //////////////////////////////////////////////////////////////////////////////////
                  // reject with the original error, then abort explicitly so the evictions above //
                  // roll back.  browsers abort on a throw from a request's handler, but          //
                  // fake-indexeddb also rethrows it as an uncaught exception.                    //
                  //////////////////////////////////////////////////////////////////////////////////
                  reject(e);
                  try
                  {
                     transaction.abort();
                  }
                  catch (abortError)
                  {
                     ////////////////////////////////////////////////////////
                     // already finished or aborted - nothing to roll back //
                     ////////////////////////////////////////////////////////
                  }
               }
            };
         });
      }
      finally
      {
         database.close();
      }

   }


   /*******************************************************************************
    ** Get the filter JSON stored under a key - or null if it isn't stored (e.g.,
    ** the URL came from another browser, the entry was evicted, or a logout
    ** deleted it), if it is older than maxAgeMs, or if IndexedDB can't be read.
    *******************************************************************************/
   public static async getFilterJSON(key: string, maxAgeMs: number = MAX_STORED_FILTER_AGE_MS): Promise<string | null>
   {
      const inMemoryFilter = ProcessFilterStorage.inMemoryFilters.get(key);
      if (inMemoryFilter && Date.now() - inMemoryFilter.storedAt <= maxAgeMs)
      {
         return (inMemoryFilter.filterJSON);
      }

      try
      {
         return await ProcessFilterStorage.withTimeout(ProcessFilterStorage.getFilterJSONWithoutTimeout(key, maxAgeMs));
      }
      catch (e)
      {
         console.log(`Could not read process filter from IndexedDB: ${e}`);
         return (null);
      }
   }


   /*******************************************************************************
    ** Do the work of getFilterJSON.  This always closes the database once it
    ** opens - even if that is after getFilterJSON's timeout has fired.
    *******************************************************************************/
   private static async getFilterJSONWithoutTimeout(key: string, maxAgeMs: number): Promise<string | null>
   {
      const database = await ProcessFilterStorage.openDatabase();
      try
      {
         return await new Promise<string | null>((resolve, reject) =>
         {
            const transaction = database.transaction([FILTER_JSON_STORE, FILTER_INFO_STORE], "readonly");
            const filterJSONRequest = transaction.objectStore(FILTER_JSON_STORE).get(key);
            const filterInfoRequest = transaction.objectStore(FILTER_INFO_STORE).get(key);
            transaction.oncomplete = () =>
            {
               /////////////////////////////////////////////////////////////////////////////
               // eviction only runs on a write, so check the age here too - otherwise a  //
               // URL in the browser's history could read a filter long past its max age. //
               /////////////////////////////////////////////////////////////////////////////
               const filterInfo = filterInfoRequest.result as StoredFilterInfo;
               if (typeof filterJSONRequest.result !== "string" || !filterInfo || Date.now() - filterInfo.storedAt > maxAgeMs)
               {
                  resolve(null);
                  return;
               }
               resolve(filterJSONRequest.result);
            };
            transaction.onerror = (event) => reject(ProcessFilterStorage.getTransactionError(transaction, event));
            transaction.onabort = (event) => reject(ProcessFilterStorage.getTransactionError(transaction, event));
         });
      }
      finally
      {
         database.close();
      }
   }


   /*******************************************************************************
    ** Delete every stored filter (by deleting the whole database).  Never rejects,
    ** and resolves within PROCESS_FILTER_STORAGE_TIMEOUT_MS even if IndexedDB is
    ** missing, fails, or hangs - so a logout can wait on it before redirecting.
    *******************************************************************************/
   public static async deleteAllFilters(): Promise<void>
   {
      ProcessFilterStorage.inMemoryFilters.clear();
      try
      {
         await ProcessFilterStorage.withTimeout(new Promise<void>((resolve, reject) =>
         {
            if (typeof indexedDB === "undefined")
            {
               resolve();
               return;
            }

            const request = indexedDB.deleteDatabase(DATABASE_NAME);
            request.onsuccess = () => resolve();
            request.onerror = () => reject(request.error);
         }));
      }
      catch (e)
      {
         console.log(`Could not delete process filters from IndexedDB: ${e}`);
      }
   }


   /*******************************************************************************
    ** Pick which stored filters to delete before storing a new one of the given
    ** length:  every one older than the max age, then the oldest of the rest,
    ** until the new one fits under the max total length.
    *******************************************************************************/
   private static chooseKeysToEvict(storedFilters: StoredFilterInfo[], newLength: number, now: number, maxTotalLength: number, maxAgeMs: number): string[]
   {
      const keysToEvict: string[] = [];
      const keptFilters: StoredFilterInfo[] = [];
      for (const storedFilter of storedFilters)
      {
         if (now - storedFilter.storedAt > maxAgeMs)
         {
            keysToEvict.push(storedFilter.key);
         }
         else
         {
            keptFilters.push(storedFilter);
         }
      }

      keptFilters.sort((a, b) => a.storedAt - b.storedAt);
      let totalLength = keptFilters.reduce((sum, storedFilter) => sum + storedFilter.length, 0);
      for (const storedFilter of keptFilters)
      {
         if (totalLength + newLength <= maxTotalLength)
         {
            break;
         }
         keysToEvict.push(storedFilter.key);
         totalLength -= storedFilter.length;
      }

      return (keysToEvict);
   }


   /*******************************************************************************
    ** Get the error a failed or aborted transaction should reject with.  An
    ** explicit abort() leaves transaction.error null, so fall back to the failing
    ** request's error, and then to a plain Error - never reject with null.
    *******************************************************************************/
   private static getTransactionError(transaction: IDBTransaction, event: Event): Error
   {
      const requestError = (event?.target as IDBRequest)?.error;
      return (transaction.error ?? requestError ?? new Error("IndexedDB transaction failed"));
   }


   /*******************************************************************************
    ** Open (creating or upgrading as needed) the database.  A blocked open just
    ** waits:  the caller's timeout gives up on it, and if it opens later, the
    ** caller still closes it.
    *******************************************************************************/
   private static openDatabase(): Promise<IDBDatabase>
   {
      return (new Promise<IDBDatabase>((resolve, reject) =>
      {
         if (typeof indexedDB === "undefined")
         {
            reject(new Error("IndexedDB is not available"));
            return;
         }

         const request = indexedDB.open(DATABASE_NAME, DATABASE_VERSION);
         request.onupgradeneeded = () =>
         {
            const database = request.result;
            if (!database.objectStoreNames.contains(FILTER_JSON_STORE))
            {
               database.createObjectStore(FILTER_JSON_STORE);
            }
            if (!database.objectStoreNames.contains(FILTER_INFO_STORE))
            {
               database.createObjectStore(FILTER_INFO_STORE);
            }
         };
         request.onsuccess = () =>
         {
            const database = request.result;
            database.onversionchange = () => database.close();
            resolve(database);
         };
         request.onerror = () => reject(request.error);
      }));
   }


   /*******************************************************************************
    ** Reject a promise that hasn't settled within PROCESS_FILTER_STORAGE_TIMEOUT_MS - so that a
    ** stuck IndexedDB can't leave a process launch or process screen waiting
    ** forever.
    *******************************************************************************/
   private static withTimeout<T>(promise: Promise<T>): Promise<T>
   {
      return new Promise<T>((resolve, reject) =>
      {
         const timeout = setTimeout(() => reject(new Error("IndexedDB timed out")), PROCESS_FILTER_STORAGE_TIMEOUT_MS);
         promise.then((value) =>
         {
            clearTimeout(timeout);
            resolve(value);
         }, (error) =>
         {
            clearTimeout(timeout);
            reject(error);
         });
      });
   }


   /*******************************************************************************
    ** Make a new random key for a stored filter.
    *******************************************************************************/
   public static makeKey(): string
   {
      if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function")
      {
         return (crypto.randomUUID());
      }
      return (Date.now().toString(36) + Math.random().toString(36).substring(2));
   }

}
